import React, { useState, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PageWidget {
  id: string;
  label: string;
  icon?: string;
  description?: string;
  defaultVisible?: boolean;
}

interface SlotState {
  id: string;
  visible: boolean;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

function load(pageId: string): SlotState[] | null {
  try { const s = localStorage.getItem(`playout_${pageId}`); return s ? JSON.parse(s) : null; }
  catch { return null; }
}

function save(pageId: string, slots: SlotState[]) {
  try { localStorage.setItem(`playout_${pageId}`, JSON.stringify(slots)); } catch {}
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePageCustomizer(pageId: string, widgets: PageWidget[]) {
  const [editing, setEditing] = useState(false);
  const [slots, setSlots] = useState<SlotState[]>(() => {
    const stored = load(pageId);
    if (stored) {
      const known = new Set(stored.map(s => s.id));
      const extras = widgets
        .filter(w => !known.has(w.id))
        .map(w => ({ id: w.id, visible: w.defaultVisible ?? true }));
      return [
        ...stored.filter(s => widgets.some(w => w.id === s.id)),
        ...extras,
      ];
    }
    return widgets.map(w => ({ id: w.id, visible: w.defaultVisible ?? true }));
  });

  const commit = useCallback((next: SlotState[]) => {
    setSlots(next);
    save(pageId, next);
  }, [pageId]);

  const toggle = useCallback((id: string) => {
    commit(slots.map(s => s.id === id ? { ...s, visible: !s.visible } : s));
  }, [slots, commit]);

  const moveUp = useCallback((idx: number) => {
    if (idx === 0) return;
    const next = [...slots];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    commit(next);
  }, [slots, commit]);

  const moveDown = useCallback((idx: number) => {
    if (idx >= slots.length - 1) return;
    const next = [...slots];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    commit(next);
  }, [slots, commit]);

  const reset = useCallback(() => {
    commit(widgets.map(w => ({ id: w.id, visible: w.defaultVisible ?? true })));
  }, [widgets, commit]);

  const isVisible = useCallback(
    (id: string) => slots.find(s => s.id === id)?.visible ?? true,
    [slots],
  );

  // Ordered list of widget defs, filtered and sorted as stored
  const orderedWidgets = slots
    .map(s => widgets.find(w => w.id === s.id))
    .filter((w): w is PageWidget => w !== undefined);

  return { editing, setEditing, slots, isVisible, orderedWidgets, toggle, moveUp, moveDown, reset };
}

// ─── Customize Button ─────────────────────────────────────────────────────────

export function CustomizeButton({
  editing,
  setEditing,
}: {
  editing: boolean;
  setEditing: (v: boolean) => void;
}) {
  return (
    <button
      onClick={() => setEditing(!editing)}
      style={{
        padding: '6px 13px',
        borderRadius: '8px',
        border: `1px solid ${editing ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
        background: editing ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
        color: editing ? '#60a5fa' : '#555555',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 0.15s',
      }}
    >
      {editing ? '✓ Done Editing' : '⚙ Customize Page'}
    </button>
  );
}

// ─── Customizer Panel (slides in from right) ──────────────────────────────────

export function CustomizerPanel({
  widgets,
  slots,
  toggle,
  moveUp,
  moveDown,
  reset,
  onClose,
}: {
  widgets: PageWidget[];
  slots: SlotState[];
  toggle: (id: string) => void;
  moveUp: (idx: number) => void;
  moveDown: (idx: number) => void;
  reset: () => void;
  onClose: () => void;
}) {
  const visibleCount = slots.filter(s => s.visible).length;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 190,
        }}
      />
      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '310px',
        background: '#0d0d0d',
        borderLeft: '1px solid rgba(255,255,255,0.1)',
        zIndex: 200,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-24px 0 60px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 16px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'flex-start', gap: '10px',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '15px', color: 'white' }}>Customize Page</div>
            <div style={{ fontSize: '11px', color: '#555555', marginTop: '3px' }}>
              {visibleCount} of {slots.length} sections visible
            </div>
          </div>
          <button
            onClick={reset}
            style={{
              padding: '4px 10px', borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'transparent', color: '#555555',
              fontSize: '11px', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Reset
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)', border: 'none',
              borderRadius: '8px', color: '#555555',
              width: '30px', height: '30px', fontSize: '14px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* Info banner */}
        <div style={{ padding: '10px 16px', background: 'rgba(59,130,246,0.06)', borderBottom: '1px solid rgba(59,130,246,0.12)' }}>
          <div style={{ fontSize: '11px', color: '#555555', lineHeight: 1.5 }}>
            Use ▲▼ to reorder sections. Toggle the switch to show or hide a section. Changes are saved automatically per device.
          </div>
        </div>

        {/* Slots list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
          {slots.map((slot, idx) => {
            const widget = widgets.find(w => w.id === slot.id);
            if (!widget) return null;
            return (
              <div
                key={slot.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 12px', borderRadius: '10px', marginBottom: '4px',
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${slot.visible ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.03)'}`,
                  opacity: slot.visible ? 1 : 0.5,
                  transition: 'opacity 0.2s',
                }}
              >
                {/* Reorder arrows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <button
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    style={{
                      background: 'none', border: 'none',
                      cursor: idx === 0 ? 'default' : 'pointer',
                      color: idx === 0 ? '#111111' : '#555555',
                      fontSize: '10px', padding: '1px 4px', lineHeight: 1,
                    }}
                  >▲</button>
                  <button
                    onClick={() => moveDown(idx)}
                    disabled={idx === slots.length - 1}
                    style={{
                      background: 'none', border: 'none',
                      cursor: idx === slots.length - 1 ? 'default' : 'pointer',
                      color: idx === slots.length - 1 ? '#111111' : '#555555',
                      fontSize: '10px', padding: '1px 4px', lineHeight: 1,
                    }}
                  >▼</button>
                </div>

                {widget.icon && (
                  <span style={{ fontSize: '15px', flexShrink: 0 }}>{widget.icon}</span>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px', fontWeight: 600,
                    color: slot.visible ? 'white' : '#555555',
                  }}>{widget.label}</div>
                  {widget.description && (
                    <div style={{ fontSize: '10px', color: '#222222', marginTop: '1px' }}>
                      {widget.description}
                    </div>
                  )}
                </div>

                {/* Toggle switch */}
                <button
                  onClick={() => toggle(slot.id)}
                  style={{
                    width: '40px', height: '22px', borderRadius: '11px', border: 'none',
                    cursor: 'pointer', flexShrink: 0,
                    background: slot.visible ? '#3b82f6' : 'rgba(255,255,255,0.08)',
                    position: 'relative', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: '3px',
                    left: slot.visible ? '21px' : '3px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    background: 'white', transition: 'left 0.2s',
                  }} />
                </button>
              </div>
            );
          })}
        </div>

        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          fontSize: '11px', color: '#222222', textAlign: 'center',
        }}>
          Saved automatically · Per-device settings
        </div>
      </div>
    </>
  );
}
